import { useEffect, useRef, useState } from 'react';

import { App, Form } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useClient } from 'src/api/axios/config';
import MobileDialog from 'src/components/MobileDialog';
import PageLayout from 'src/components/PageLayout';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { useGlobal } from 'src/contexts/GlobalContext';
import { useTheme } from 'src/contexts/ThemeContext';
import { useDeleteClientAsset, useGetClientAssets, useUpdateClientAssets } from 'src/hooks/api/useClientsEndpoint';
import { useModal } from 'src/hooks/useModal';
import SettingCard from 'src/pages/Devices/DeviceDetails/TabPages/SensorSettings/components/SettingCard';
import { sanitizeFileName } from 'src/utils/sanitize';

import AssetFileInput from './components/AssetFileInput';
import AssetsList from './components/AssetsList';
import {
  AssetFormValue,
  checkForDuplicateAsset,
  FileData,
  getContentTypeFromDataUrl,
  mapApiAssetsToFormValues,
  stripDataUrlPrefix,
} from './utils';

export const Assets = () => {
  const { t } = useTranslation();
  const { isDesktop } = useGlobal();
  const { combinedTheme } = useTheme();
  const client = useClient();
  const { data: assets, isLoading, isFetching } = useGetClientAssets(client);
  const { mutateAsync: updateAssets } = useUpdateClientAssets();
  const { mutateAsync: deleteAsset } = useDeleteClientAsset();
  const { confirm, contextHolder } = useModal();
  const { message } = App.useApp();
  const [isDrawerVisible, setDrawerVisible] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const formMethods = useForm<{ assets: AssetFormValue[] }>({
    defaultValues: {
      assets: [],
    },
  });

  useEffect(() => {
    if (assets && !isFetching) {
      const formAssets = mapApiAssetsToFormValues(assets);
      formMethods.reset({ assets: formAssets });
    }
  }, [assets, isFetching, formMethods]);

  const validateFile = (fileData: { url?: string; image?: string; name?: string; size?: number | string }): boolean => {
    const currentAssets = formMethods.getValues().assets;
    const isDuplicate = checkForDuplicateAsset(fileData, currentAssets);

    if (isDuplicate) {
      message.error(t('assets.duplicateError'));
    }

    return !isDuplicate;
  };

  const handleAddAsset = async (fileData: FileData | string | null) => {
    if (!fileData) return;

    const currentAssets = formMethods.getValues().assets;
    let newAsset: AssetFormValue = { name: '', file: '', contentType: '' };

    if (typeof fileData === 'object') {
      const file = fileData.url || fileData.image || '';
      const name = sanitizeFileName(fileData.name || '');
      const contentType = getContentTypeFromDataUrl(file);
      const base64 = stripDataUrlPrefix(file);
      newAsset = { name, file: base64, contentType };
    } else if (typeof fileData === 'string') {
      const base64 = stripDataUrlPrefix(fileData);
      newAsset = {
        name: '',
        file: base64,
        contentType: getContentTypeFromDataUrl(fileData),
      };
    }

    const emptySlotIndex = currentAssets.findIndex((asset) => !asset.file && !asset.name);

    if (emptySlotIndex !== -1) {
      const newAssets = [...currentAssets];
      newAssets[emptySlotIndex] = { ...newAsset, id: 'loading' };
      formMethods.setValue('assets', newAssets);
    }

    try {
      await updateAssets(newAsset);
    } catch (error) {
      console.error('Error saving asset:', error);
    }
  };

  const handleRemoveAsset = (id: string) => {
    const deleteAssetAndUpdate = async () => {
      try {
        await deleteAsset(id);
      } catch (error) {
        console.error('Error deleting asset:', error);
      }
    };

    if (isDesktop) {
      confirm({
        title: t('assets.deleteAsset'),
        onOk: async () => await deleteAssetAndUpdate(),
      });
    } else {
      MobileDialog({
        title: t('assets.deleteAsset'),
        description: t('general.cannotBeUndone'),
        confirmText: t('general.ok').toUpperCase(),
        cancelText: t('general.cancel'),
        onConfirm: deleteAssetAndUpdate,
        theme: combinedTheme,
      });
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleAddAsset(file);
    }
    e.target.value = '';
  };

  const handleChooseFromLibrary = () => {
    setDrawerVisible(false);
    fileInputRef.current?.click();
  };

  const items = [
    {
      header: t('assets.assetFiles'),
      content: (
        <AssetsList
          assets={formMethods.watch('assets')}
          onChange={handleAddAsset}
          onRemove={handleRemoveAsset}
          onDrawerOpen={() => setDrawerVisible(true)}
          isDesktop={isDesktop}
          fileInputRef={fileInputRef}
          isDrawerVisible={isDrawerVisible}
          setDrawerVisible={setDrawerVisible}
          handleChooseFromLibrary={handleChooseFromLibrary}
          t={t}
          handleDeleteAsset={handleRemoveAsset}
          handleSlotChange={handleAddAsset}
          formMethods={formMethods}
          onValidateFile={validateFile}
        />
      ),
    },
  ];

  const action = null;

  return (
    <FormProvider {...formMethods}>
      {contextHolder}
      <Form layout="vertical">
        <PageLayout title={t('assets.title')} action={action}>
          {isLoading ? (
            <SuspenseLoader />
          ) : (
            <SettingCard title={t('assets.siteAssets')} description={t('assets.siteAssetsDescription')} items={items} />
          )}
        </PageLayout>
        {!isDesktop && <AssetFileInput fileInputRef={fileInputRef} onChange={handleFileInputChange} />}
      </Form>
    </FormProvider>
  );
};
